/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {addToast, Toast} from '@react/react-spectrum/Toast';
import AppAssemblyService from './services/AppAssemblyService';
import {AuthInstance, getBestSupportedLocale} from '@exc/auth';
import CoreContext from './context';
import discoveryUtil from './utils/discovery';
import {getData, GraphQlService} from '@exc/graphql';
import {getOrgName, getTenantFromPath} from './utils/org';
import {history} from '@exc/router';
import {I18nShellNoAccess} from '@exc/shell';
import Metrics from '@exc/metrics';
import {Internal as NetworkInternal} from '@exc/runtime/network';
import {NOOP} from './utils/coreConstants';
import PropTypes from 'prop-types';
import PulseWrapper from '@engage-ui/pulse-v2/dist/pulse/PulseWrapper';
import React from 'react';
import SettingsService, {SETTINGS_LEVEL} from '@exc/settings';
import ShellWrapper from './Shell';
import supportedBrowsers from './utils/supportedBrowsers';
import TranslationService from './services/TranslationService';
import urlManager, {getPathWithoutTenant, getQueryValue} from './utils/URLManager';
import {withLocalization} from '@adobe/adobe-intl-react';

const DEFAULT_HERO = {
  icon: null,
  shortTitle: '',
  title: ''
};

const DEFAULT_HELPCENTER = {
  enabled: true
};

const TEN_HOURS = 1000 * 60 * 60 * 10;

const UNIFIED_SHELL_APPID = 'shell';

class Core extends React.Component {
  constructor(props) {
    super(props);

    this.config = props.config;
    this.metrics = new Metrics('exc.core.Core');
    this.shellTimer = props.shellTimer || this.metrics.start('Shell');

    this.state = {
      accessToken: null,
      auth: null,
      customEnvLabel: '',
      customHeroClick: false,
      customSearch: {},
      defaultComponent: this.config.defaultComponent,
      environment: this.config.environment,
      favicon: this.config.favicon,
      feedback: {},
      helpCenter: DEFAULT_HELPCENTER,
      hero: DEFAULT_HERO,
      imsOrgs: [],
      modal: false,
      nps: {
        enabled: false,
        sampling: 10,
        showToAdobeUsers: true
      },
      onCustomButtonClick: NOOP,
      orgsMap: {},
      orgToSubOrgsMap: {},
      profile: null,
      selectedImsName: this.config.selectedImsName,
      selectedImsOrg: this.config.selectedImsOrg,
      selectedSubOrg: null,
      shellResponse: {},
      solutionConfig: {},
      supportsViewportTakeover: false,
      viewportTakeover: false,
      workspaces: []
    };

    const {endpoints} = this.config;
    this.translationService = new TranslationService(endpoints.translation);
    this.history = props.history || history;
    this.interval = this.config.authInterval;
    this.orgTenantMap = {};
    this.tenantOrgMap = {};
    this.userServiceCodes = {};
    this.unlisten = this.history.listen(this.onHistoryChange);
    this.settingsHandler = this.settingsHandler.bind(this);
    this.settingsRef = {
      SETTINGS_LEVEL,
      settingsHandler: this.settingsHandler
    };
  }

  async componentDidMount() {
    // Check if the current browser is supported and show a message if it's not.
    if (!supportedBrowsers.test(window.navigator.userAgent)) {
      this.showToast('browserlistWarning', {variant: 'info'});
    }
    const auth = await AuthInstance();
    this.setState({auth}, () => this.authProcessing());
  }

  componentWillUnmount() {
    this.unlisten && this.unlisten();
    this.validate && clearInterval(this.validate);
  }

  async authProcessing() {
    const authProcessingTimer = this.metrics.start('AuthProcessing');
    const {auth} = this.state;
    // Remove the "auth.selectedimsorg" event listener because the metrics and
    // state will be updated in onImsOrgChange.
    auth.removeListenersFromAuth(['auth.selectedimsorg']);

    let profile = auth.fetchUserProfile();
    let accessToken = auth.fetchAccessToken();
    const locale = getBestSupportedLocale(profile, this.props.supportedLocales);
    const productContext = profile.projectedProductContext;

    document.documentElement.setAttribute('lang', locale);

    // Grab the tenant ids from the product contexts for all orgs this user
    // is a part of. This will be used to map org id values to tenants for
    // the @value within the path.
    productContext.forEach(ctx => {
      const {owningEntity, serviceCode, tenant_id} = ctx.prodCtx;

      if (serviceCode === 'dma_tartan') {
        this.orgTenantMap[owningEntity] = tenant_id;
        this.tenantOrgMap[tenant_id] = owningEntity;
      }
      // create map of org id to list of service codes
      if (this.userServiceCodes[owningEntity]) {
        this.userServiceCodes[owningEntity].push(serviceCode);
      } else {
        this.userServiceCodes[owningEntity] = [serviceCode];
      }
    });

    const fallbackOrgId = this.getFallbackOrg(Object.keys(this.orgTenantMap));

    // Grab the org from the path and use that as the selected org. If there
    // is no org yet, use the selected one from IMS.
    let pathTenant = getTenantFromPath(this.history.location.pathname);
    let pathOrg = pathTenant;
    if (pathTenant && pathTenant.indexOf('AdobeOrg') === -1) {
      pathOrg = this.tenantOrgMap[pathTenant];
      pathOrg || this.metrics.error(`User doesn't have access to ${pathTenant} tenant`);
    }

    const tenantInPath = !!pathOrg;
    const clientId = auth.getClientId();
    const environment = this.config.serviceEnvironment;

    //configure network runtime API
    NetworkInternal.configure(Object.assign({}, {
      environment,
      getTokenInfo: () => {
        return Promise.resolve({
          token: auth.fetchAccessToken()
        });
      },
      imsClientId: clientId
    }, this.state.solutionConfig));

    this.settingsService = new SettingsService({
      env: environment,
      imsInfo: {accessToken, clientId}
    });

    if (!this.isLoginFlow()) {
      // Hydrate Gql Cache with data required at app reload for usage by consuming modules
      const gqlService = new GraphQlService(environment, clientId, accessToken);
      try {
        await gqlService.initializeShellDataForReload({
          apiKey: clientId,
          config_version: 0,
          fallbackOrgId,
          keys: ['lastClickTimestamp', 'lastSeenAnnouncementsTimestamp', 'lastNotificationSeenTimestamp'],
          locale,
          sessionUrl: profile.session,
          userCurrentOrgId: pathOrg || this.props.org || null,
          userId: profile.userId
        });
      } catch (error) {
        if (error && error.status === 401) {
          return await this.onSignOut();
        }
      }
    }

    const orgFromIms = this.props.org;
    const gqlCachedUserOrgs = getData('userOrgs');
    let [avatar, currentImsOrg, orgsMap, session] = await Promise.all([
      auth.fetchAvatar(),
      orgFromIms || auth.fetchCurrentOrg(),
      gqlCachedUserOrgs ? gqlCachedUserOrgs : auth.fetchOrgsMap(),
      auth.getSession()
    ]);
    profile.avatar = avatar;

    // Adding `dma_tartan` filtering for the orgsMap here in case it's not
    // filtered properly in GraphQL. `orgTenantMap` only has orgs that have the
    // `dma_tartan` service code.
    orgsMap = orgsMap.filter(({orgRef}) => this.orgTenantMap[`${orgRef.ident}@${orgRef.authSrc}`]);

    const selectedImsOrg = pathOrg || orgFromIms || currentImsOrg || fallbackOrgId;
    const tenantId = this.orgTenantMap[selectedImsOrg] || selectedImsOrg;
    this.metrics.event('Auth.done', {
      defaultOrg: orgFromIms || '',
      defaultTenant: this.orgTenantMap[orgFromIms || ''] || '',
      selectedOrg: selectedImsOrg,
      selectedTenant: tenantId
    });

    let localSession = JSON.parse(window.localStorage.getItem('unifiedShellSession')) || {};
    const currentActiveProductContext = localSession.activeProductContext || session.current_active_pc || {};
    const activeProductContext = this.getActiveProductContext(currentActiveProductContext, productContext);

    const wasTenantSelected = pathOrg === currentImsOrg;
    if (!selectedImsOrg) {
      this.metrics.info('selectedImsOrg undefined in Core.js authProcessing');
      this.setState({locale, showNoAccessShell: true});
      return;
    }
    const selectedImsOrgName = getOrgName(selectedImsOrg, orgsMap);
    // Falls back to the org id if there is no tenant available for the org
    window.adobeMetrics.setUser({
      accountType: profile.account_type,
      authSystem: 'ims',
      data: {tenantId},
      displayName: profile.displayName,
      groupId: selectedImsOrg,
      groupName: selectedImsOrgName,
      language: locale,
      sessionId: profile.session,
      userId: profile.userId
    });

    let orgIncludedInList = orgsMap.find(org => `${org.orgRef.ident}@AdobeOrg` === selectedImsOrg);
    if (!orgIncludedInList) {
      this.metrics.error(`selectedImsOrg ${selectedImsOrg} not included in orgsMap in Core.js authProcessing`);
    }

    let imsOrgs = orgsMap.map(org => ({
      label: org.orgName,
      value: `${org.orgRef.ident}@${org.orgRef.authSrc}`
    }));
    imsOrgs.sort((a, b) => a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1);

    auth.setupValidate(this.interval);

    // Instantiate the App Assembly Service when we have the values we need
    this.appAssemblyService = new AppAssemblyService({
      environment,
      imsOrgs,
      locale,
      orgTenantMap: this.orgTenantMap,
      profile,
      session,
      userRoles: profile.roles,
      userServiceCodes: this.userServiceCodes
    });

    // Redirect to profile page if profile is in path
    if (getPathWithoutTenant(window.location.hash.replace(/^#/, '')) === '/profile') {
      return window.location.assign(this.appAssemblyService.getProfileComponent(tenantId).editProfileLink);
    }

    this.setState(
      {
        accessToken,
        activeProductContext,
        imsOrgs,
        locale,
        orgsMap,
        profile,
        selectedImsOrg,
        selectedImsOrgName,
        session
      },
      async () => {
        // The tenant is in the URL and it was not the current IMS org in the
        // session. Need to trigger an org change to let IMS know that this is
        // the current org.
        if (tenantInPath && !wasTenantSelected) {
          await this.onImsOrgChange(selectedImsOrg, currentImsOrg, true);
        } else {
          // The tenant is not in the URL or it is and was already the current IMS
          // org in the session. Get shell configuration and update the URL.
          let shellResponse = this.getShellConfiguration(selectedImsOrg, selectedImsOrgName);

          this.setState({shellResponse});
          urlManager.updateTenantAndPath(tenantId, null, true);
          const settingsResponse = getData('userOrgSettings', selectedImsOrg, locale);
          const timelineResponse = getData('timeline', selectedImsOrg, locale);
          await PulseWrapper.initPulse(accessToken, selectedImsOrg, environment, locale,
            shellResponse, this.settingsRef, false, timelineResponse, settingsResponse);

          // If the org isn't authorized for the solution, redirect to home
          const isPermissioned = this.isOrgPermissionedOnSolution(selectedImsOrg);
          const path = isPermissioned ? null : '/home';
          urlManager.updateTenantAndPath(tenantId, path, true);
          isPermissioned || this.showToast('orgHomeRedirectToast', {timeout: 8000, variant: 'info'});
        }

        authProcessingTimer.time('done');
        this.shellTimer.time(this.isLoginFlow() ? 'isLoginFlow.done' : 'done', {
          gqlEnabled: true,
          region: window.config && window.config.region
        }); // step 9 ready
      }
    );
  }

  isLoginFlow() {
    const pathname = window.document.referrer;
    // If this is coming here after the login workflow, there will be
    // a `ims/slo_redirect` param in the path.
    return pathname && pathname.indexOf('ims/slo_redirect') > -1;
  }

  /**
   * Get a fallback org in a standard way. Currently it sorts the list of org
   * ids in ascending order and takes the first one.
   * @param {string[]} orgs List of org ids.
   * @returns {string} The fallback org.
   */
  getFallbackOrg = orgs => orgs.sort()[0]

  getActiveProductContext = (currentActiveProductContext, productContext) => {
    let activeProductContext = {};
    Object.keys(currentActiveProductContext).map(key => {
      const pc = productContext.find(ctx => ctx.prodCtx.ident === currentActiveProductContext[key].ident) || {};
      activeProductContext[key] = pc.prodCtx;
    });
    return activeProductContext;
  }

  getComponentContext() {
    const {
      activeProductContext,
      accessToken,
      customSearch,
      environment,
      favicon,
      imsOrgs,
      locale,
      profile,
      selectedImsOrg,
      selectedImsOrgName,
      selectedSubOrg,
      session,
      shellResponse
    } = this.state;

    return {
      activeProductContext,
      adobeIMS: window.adobeIMS,
      customSearch,
      environment,
      favicon,
      formatMessage: this.props.formatMessage,
      getCachedToken: this.getCachedToken,
      imsClientId: this.config.ims.client_id,
      imsEnvironment: this.config.ims.environment,
      imsOrg: selectedImsOrg,
      imsOrgName: selectedImsOrgName,
      imsOrgs,
      imsProfile: profile,
      imsToken: accessToken,
      locale,
      metricsEnv: this.props.metricsEnv,
      onSignOut: this.onSignOut,
      preferredLanguages: profile && profile.preferred_languages || [],
      resetConfigStates: (appId, nextAppId) => this.resetConfigStates(appId, nextAppId),
      session,
      setBasePath: this.setVal('basePath'),
      setCustomButtonClick: this.setVal('onCustomButtonClick'),
      setCustomButtons: this.setVal('customButtons'),
      setCustomEnvLabel: this.setVal('customEnvLabel'),
      setCustomHeroClick: this.setVal('customHeroClick'),
      setCustomSearch: this.setCustomSearch,
      setDigitalData: this.setVal('digitalData'),
      setFeedbackConfig: this.setVal('feedback'),
      setHelpCenter: this.setHelpCenter,
      setHero: this.setVal('hero'),
      setLogoutUrl: url => this.state.auth.logout.add(url),
      setModal: this.setVal('modal'),
      setNPSConfig: this.setNPSConfig,
      setSession: this.setSession,
      setSolutionConfig: this.setSolutionConfig,
      setSolutionLoaded: this.setVal('solutionLoaded'),
      setSubOrgs: this.setSubOrgs,
      settingsService: this.settingsRef,
      setWorkspaces: this.setVal('workspaces'),
      shellHeight: 48, // Default height of the shell
      shellInfo: shellResponse,
      subOrg: selectedSubOrg,
      supportsViewportTakeover: this.setVal('supportsViewportTakeover'),
      tenant: (this.orgTenantMap || {})[selectedImsOrg] || '',
      toggleViewportTakeover: this.setVal('viewportTakeover'),
      translationService: this.translationService,
      updateCachedTokens: this.updateCachedTokens
    };
  }

  resetConfigStates = (appId, nextAppId) => {
    // Same app is being loaded next. Don't reset so experience doesn't shift.
    if (appId === nextAppId) {
      return;
    }

    this.setState({
      customButtons: [],
      customEnvLabel: '',
      customHeroClick: null,
      customSearch: {enabled: false, open: false},
      digitalData: null,
      feedback: {},
      helpCenter: null,
      onCustomButtonClick: NOOP,
      orgToSubOrgsMap: {},
      workspaces: []
    });
  }

  getShellConfiguration(selectedImsOrg, selectedImsOrgName) {
    let selectedOrg = selectedImsOrg || this.state.selectedImsOrg;
    let expiry = this.state.auth.fetchAccessTokenInfo();
    return this.appAssemblyService.fetchShell(
      this.state.accessToken,
      expiry.expires_in,
      selectedOrg,
      selectedImsOrgName || this.state.selectedImsName,
      this.orgTenantMap[selectedOrg]
    );
  }

  onImsOrgChange = async (selectedImsOrg, currentImsOrg, replace, selectedSubOrg) => {
    const {auth, orgsMap} = this.state;
    let shellResponse;

    if (selectedImsOrg === currentImsOrg) {
      return this.setState({selectedSubOrg});
    }

    try {
      auth.changeOrg(selectedImsOrg, currentImsOrg);
      const selectedImsOrgName = getOrgName(selectedImsOrg, orgsMap);
      shellResponse = this.getShellConfiguration(selectedImsOrg, selectedImsOrgName);
      window.adobeMetrics.setUser({
        data: {tenantId: this.orgTenantMap[selectedImsOrg]},
        groupId: selectedImsOrg,
        groupName: selectedImsOrgName
      });
      this.setState({selectedImsOrg, selectedImsOrgName, selectedSubOrg, shellResponse});
    } catch (error) {
      this.metrics.error(error.message);
      this.showToast('orgFailToast', {variant: 'error'});
      return;
    }

    // Based on code flow, it's possible the Pulse instance needs to be
    // initialized here instead of just org changed.
    if (PulseWrapper.isPulseReady()) {
      PulseWrapper.handleIMSOrgChange(selectedImsOrg, shellResponse);
    } else {
      const {accessToken, locale} = this.state;
      PulseWrapper.initPulse(accessToken, selectedImsOrg, this.config.serviceEnvironment,
        locale, shellResponse, this.settingsRef);
    }

    // If the org isn't authorized for the solution, redirect to home
    const isPermissioned = this.isOrgPermissionedOnSolution(selectedImsOrg);
    const path = isPermissioned ? null : '/home';

    urlManager.updateTenantAndPath(this.orgTenantMap[selectedImsOrg] || selectedImsOrg, path, replace);
    !isPermissioned && this.showToast('orgSwitchRedirectToast', {timeout: 8000, variant: 'info'});
    this.metrics.analytics.trackPage();

    // if org changed and no sub org selected, reset selectedSubOrg
    if (selectedImsOrg !== currentImsOrg && !selectedSubOrg) {
      this.setState({selectedSubOrg: {id: null, name: null}});
    }
    return true;
  }

  setSession = async (activeProductContextData) => {
    let {auth} = this.state;
    let localSession = JSON.parse(window.localStorage.getItem('unifiedShellSession')) || {};
    let session = localSession.activeProductContext ? {
      current_active_pc: localSession.activeProductContext
    } : await auth.getSession();
    let newActiveProductContext = {
      ...session.current_active_pc,
      ...activeProductContextData.data
    };
    auth.setSession({
      session_data: {
        current_active_pc: newActiveProductContext
      }
    });
    this.setState({activeProductContext: newActiveProductContext});
    localSession.activeProductContext = newActiveProductContext;
    window.localStorage.setItem('unifiedShellSession', JSON.stringify(localSession));
  }

  /**
   * Checks whether an ims org is permissioned on a given solution.
   * @param {string} orgId The IMS org.
   * @param {Object} solutionConfig Optional solution config.
   * @returns {boolean} If the org is permissioned.
   */
  isOrgPermissionedOnSolution(orgId, solutionConfig) {
    const solutionServiceCodes = (solutionConfig || this.state.solutionConfig).access || [];
    if (!solutionServiceCodes.length) {
      return true;
    }
    const userServiceCodes = this.userServiceCodes[orgId] || [];
    return solutionServiceCodes.some(code => userServiceCodes.indexOf(code) > -1);
  }

  /**
   * Displays a toast with a custom message.
   * @param {string} messageId The message ID of the localized string to populate the toast.
   * @param {Object} options Optional object of objects such as variant.
   */
  showToast(messageId, options) {
    addToast(
      <Toast closable {...options}>
        {this.props.formatMessage({
          defaultMessage: this.props.localizationStrings[messageId],
          messageId
        })}
      </Toast>
    );
  }

  onHistoryChange = (location, action) => {
    if (action !== 'POP') {
      return;
    }

    let pathTenant = getTenantFromPath(location.pathname);
    let pathOrg = this.tenantOrgMap[pathTenant];
    const {selectedImsOrg} = this.state;

    // If the org is not set, update the URL with the current one.
    if (!pathOrg) {
      setTimeout(() => urlManager.updateTenantAndPath(this.orgTenantMap[selectedImsOrg] || selectedImsOrg, null, true), 0);
    }
    // Compare the new org with the current one. If they are different,
    // trigger an org change. If there is no pathOrg, that means
    // solution has hideTenant and this can be skipped when tenant id isn't
    // the part of the history that changed.
    if (pathOrg && pathOrg !== selectedImsOrg) {
      // Replace argument is `true` because the URL has already been updated,
      // so we don't want to push anything else to the history.
      return this.onImsOrgChange(pathOrg, selectedImsOrg, true);
    }
  }

  onSignOut = async (authExpired, message = 'User signed out') => {
    // Clear Discovery Url Cache
    discoveryUtil.clear();
    sessionStorage.removeItem('prevSrc');

    this.metrics.event('Shell.signOut', undefined, message);
    window.adobeMetrics.clearUser();
    await this.state.auth.signOut(authExpired);
  }

  onCustomSearchClick = () => {
    const {enabled, onClick, open} = this.state.customSearch;
    open && this.metrics.analytics.trackEvent({
      action: 'click',
      element: 'custom search',
      feature: 'shell header',
      type: 'button',
      widget: {name: 'custom search', type: 'button'}
    });
    this.setState({customSearch: {enabled, onClick, open: !open}});
  }

  setHelpCenter = (value = DEFAULT_HELPCENTER) => {
    this.setState(prev => ({
      helpCenter: {
        ...prev.helpCenter,
        ...value
      }
    }));
  }

  setSolutionConfig = (solutionConfig) => {
    const state = {
      helpCenter: {
        ...DEFAULT_HELPCENTER,
        solution: solutionConfig.appId
      },
      nps: this.getNps(solutionConfig.nps),
      solutionConfig
    };
    solutionConfig.hero && (state.hero = solutionConfig.hero);
    this.setState(state);

    const {selectedImsOrg} = this.state;
    // Check if the org is allowed to access the new solution.
    if (selectedImsOrg && !this.isOrgPermissionedOnSolution(selectedImsOrg, solutionConfig)) {
      const tenant = this.orgTenantMap[selectedImsOrg] || selectedImsOrg;
      urlManager.updateTenantAndPath(tenant, '/home', true);
      return this.showToast('orgHomeRedirectToast', {timeout: 8000, variant: 'info'});
    }
    urlManager.setUrlHasTenant(!solutionConfig.hideTenant);
  }

  getNps = newNps => {
    const forceNps = getQueryValue('forcenps');
    const nps = Object.assign({enabled: false, sampling: 10, showToAdobeUsers: true}, newNps || {});
    if (forceNps) {
      Object.assign(nps, {
        force: forceNps === 'true'
      });
    }
    return nps;
  }

  setCustomSearch = (value) => {
    let customSearch = {
      // If an enabled value is not explicitely set, maintain the current state
      enabled: value.enabled === undefined ? this.state.customSearch.enabled : value.enabled,
      onClick: this.onCustomSearchClick,
      open: value.open || false
    };

    this.setState({customSearch});
  }

  setNPSConfig = (value) => {
    let nps = this.getNps(value);
    this.setState({nps});
  }

  setSubOrgs = (orgToSubOrgsMap, selectedSubOrg) => {
    this.setState({orgToSubOrgsMap});
    if (!selectedSubOrg) {
      return;
    }

    let parentOrg;
    for (let orgId in orgToSubOrgsMap) {
      for (let subOrg of orgToSubOrgsMap[orgId]) {
        if (subOrg.id === selectedSubOrg.id) {
          parentOrg = orgId;
          break;
        }
      }
    }

    if (parentOrg !== this.state.selectedImsOrg) {
      throw Error('selectedSubOrg is not a child of the current ims org');
    }

    this.setState({selectedSubOrg});
  }

  /**
   * Read/Upsert/Delete settings.
   * @param {Object} data Required: JSONObject contaning requested groupId,
   * settings, and corresponding settings level(optional).
   * GET ex- {groupId: 'group1', level: SETTINGS_LEVEL.USER,
   * settings: {key1: default value to fallback}}.
   * POST ex- {groupId: 'group1', settings: {key1: value1}}.
   * @param {string} method Required: Method Type: GET/POST/DELETE.
   * @param {Object} isUnifiedShell Optional: To be set to true
   * by unified shell apps to consume Unified Shell Application space.
   * @param {Object} appId - Optional: Used by unifiedShell apps to
   * consume settings from different app space overriding unified shell app space.
   * ex- settingsHandler(data, 'GET', false, 'not-unifiedshell-appId')
   *
   * @returns {Promise} - Settings Response.
   */
  async settingsHandler(data, method, isUnifiedShell, appId) {
    const {solutionConfig, selectedImsOrg} = this.state;
    return await this.settingsService.settingsHandler({
      appId: appId || isUnifiedShell && UNIFIED_SHELL_APPID
        // pick solutionConfig.settingsAppId if present rather than solutionConfig.appId
        || solutionConfig.settingsAppId || solutionConfig.appId,
      data,
      imsOrgId: selectedImsOrg,
      method
    });
  }

  setVal(type) {
    return val => this.setState({[type]: val});
  }

  /**
   * Gets cached token for sandbox re-auth. Should retrieve from cache if
   * switching solutions within 10 minutes.
   * @param {string} clientId Client id string.
   * @param {string} scope List of scopes.
   * @returns {Object} Profile, token, and date info.
   */
  getCachedToken = (clientId, scope) => {
    let cachedTokens = JSON.parse(localStorage.getItem('shellCachedTokens')) || {};
    let key = btoa(clientId + scope);

    if (!(key in cachedTokens)) {
      return;
    }

    let info = cachedTokens[key];
    let dateDiff = Date.now() - info.dateStored;
    if (dateDiff < TEN_HOURS) {
      this.updateCachedTokens(clientId, scope, info);
      return info;
    }
    // clear expired token by passing no token
    this.updateCachedTokens(clientId, scope);
  }

  /**
   * Sets and updates cached token for sandbox re-auth. Stores token with
   * current date using clientId and scopes.
   * @param {string} clientId Client id string.
   * @param {string} scope List of scopes.
   * @param {Object} ims Ims object with profile and token info.
   */
  updateCachedTokens = (clientId, scope, ims) => {
    let cachedTokens = JSON.parse(localStorage.getItem('shellCachedTokens')) || {};
    let key = btoa(clientId + scope);
    if (!ims) {
      delete cachedTokens[key];
    } else {
      cachedTokens[key] = {
        dateStored: Date.now(),
        profile: ims.profile,
        token: ims.token
      };
    }
    localStorage.setItem('shellCachedTokens', JSON.stringify(cachedTokens));
  }
  render() {
    const {children, providerRef} = this.props;
    const {
      accessToken,
      basePath,
      customEnvLabel,
      customHeroClick,
      customButtons,
      customSearch,
      defaultComponent,
      digitalData,
      environment,
      feedback,
      helpCenter,
      hero,
      imsOrgs,
      locale,
      modal,
      nps,
      onCustomButtonClick,
      orgToSubOrgsMap,
      profile,
      selectedImsOrg,
      selectedSubOrg,
      shellResponse,
      showNoAccessShell,
      solutionConfig,
      solutionLoaded,
      supportsViewportTakeover,
      viewportTakeover,
      workspaces
    } = this.state;

    if (showNoAccessShell) {
      return <I18nShellNoAccess locale={locale} wait={defaultComponent} />;
    }

    // This does the equivalent of a lodash groupBy(customButtons, 'scope');
    const scopedCustomButtons = (customButtons || []).reduce((r, v, i, a, k = v['scope']) => ((r[k] || (r[k] = [])).push(v), r), {});
    return (
      <CoreContext.Provider value={this.getComponentContext()}>
        <ShellWrapper
          accessToken={accessToken}
          appId={solutionConfig.appId}
          basePath={basePath}
          clientId={this.config.ims.client_id}
          container={providerRef.current}
          customButtons={scopedCustomButtons}
          customEnvLabel={customEnvLabel}
          customHeroClick={customHeroClick}
          customSearch={customSearch}
          defaultComponent={defaultComponent}
          digitalData={digitalData}
          environment={environment}
          feedback={feedback}
          helpCenter={helpCenter}
          hero={hero}
          imsOrgs={imsOrgs}
          locale={locale}
          modal={modal}
          noSolution={!solutionConfig || !solutionConfig.path}
          nps={nps}
          onCustomButtonClick={onCustomButtonClick}
          onImsOrgChange={this.onImsOrgChange}
          onSignOut={this.onSignOut}
          orgToSubOrgsMap={orgToSubOrgsMap}
          profile={profile}
          selectedImsOrg={selectedImsOrg}
          selectedSubOrg={selectedSubOrg}
          selectedTenantId={this.orgTenantMap[selectedImsOrg]}
          serviceEnvironment={this.config.serviceEnvironment}
          settingsService={this.settingsRef}
          shellResponse={shellResponse}
          solutionLoaded={solutionLoaded}
          supportsViewportTakeover={supportsViewportTakeover}
          translationService={this.translationService}
          viewportTakeover={viewportTakeover}
          workspaces={workspaces}
        >
          {children}
        </ShellWrapper>
      </CoreContext.Provider>
    );
  }
}

Core.defaultProps = {
  localizationStrings: {
    browserlistWarning: 'Your browser is not supported. Consider updating your browser to the latest version to ensure the best experience.',
    orgFailToast: 'Switching organizations encountered an error. Please try again.',
    orgHomeRedirectToast: 'You don\'t have access to the linked application for that organization and have been redirected to the Experience Cloud home page.',
    orgSwitchRedirectToast: 'You don\'t have access to that application in your selected organization and have been redirected to the Experience Cloud home page.',
    solutionIframe: 'Main Content'
  }
};

Core.propTypes = {
  config: PropTypes.object,
  isLogin: PropTypes.bool,
  localizationStrings: PropTypes.shape({
    /** Toast message displayed to user if their browser is not supported */
    browserlistWarning: PropTypes.string,
    /** Toast message displayed to user if org change fails */
    orgFailToast: PropTypes.string,
    /** Toast message displayed when user is redirected home because of lack of access in IMS org */
    orgHomeRedirectToast: PropTypes.string,
    /** Toast message displayed when user is redirected home during org switch because of lack of access in IMS org */
    orgSwitchRedirectToast: PropTypes.string,
    /** Name attribute for Sandbox Iframe indicating it is the "Main Content" of the page*/
    solutionIframe: PropTypes.string
  }),
  metrics: PropTypes.object,
  org: PropTypes.string,
  providerRef: PropTypes.object,
  supportedLocales: PropTypes.arrayOf(PropTypes.string)
};

export default withLocalization(Core);
